import { Command } from 'cmdk'
import { Check, Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import { CURRENCIES, POPULAR_CURRENCIES } from '@globalx/core'
import { useFormat } from '@/lib/format'
import { useConverter } from '@/store/converter'
import { CurrencyFlag } from './CurrencyFlag'

const groupHeading =
  '[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-[0.7rem] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-subtle [&_[cmdk-group-heading]]:uppercase'

function Row({
  code,
  group,
  selected,
  onSelect,
}: {
  code: string
  group: string
  selected?: boolean
  onSelect: (code: string) => void
}) {
  const { t } = useTranslation()
  const { currencyName } = useFormat()
  const favorites = useConverter((s) => s.favorites)
  const toggleFavorite = useConverter((s) => s.toggleFavorite)
  const fav = favorites.includes(code)
  const name = currencyName(code)
  return (
    <Command.Item
      value={`${group}:${code}`}
      keywords={[code, name]}
      onSelect={() => onSelect(code)}
      className="group flex h-14 cursor-pointer items-center gap-3 rounded-xl px-3 outline-none data-[selected=true]:bg-surface-2"
    >
      <CurrencyFlag code={code} className="size-8" />
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="text-sm font-semibold">{code}</span>
        <span className="truncate text-xs text-muted">{name}</span>
      </span>
      {selected && <Check className="size-4 text-gold-text" aria-hidden />}
      <button
        type="button"
        aria-label={fav ? t('converter.removeFavorite') : t('converter.addFavorite')}
        aria-pressed={fav}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          toggleFavorite(code)
        }}
        className={clsx(
          'inline-flex size-9 items-center justify-center rounded-lg transition hover:bg-surface-3',
          fav ? 'text-gold-400' : 'text-subtle',
        )}
      >
        <Star className="size-4" fill={fav ? 'currentColor' : 'none'} />
      </button>
    </Command.Item>
  )
}

/** Searchable currency list (by ISO code or localized name), shared by picker and global search. */
export function CurrencyCommandList({
  query,
  selected,
  onSelect,
}: {
  query: string
  selected?: string
  onSelect: (code: string) => void
}) {
  const { t } = useTranslation()
  const favorites = useConverter((s) => s.favorites)
  const searching = query.trim().length > 0
  return (
    <>
      <Command.Empty className="px-4 py-10 text-center text-sm text-muted">
        {t('converter.noResults', { q: query })}
      </Command.Empty>
      {!searching && favorites.length > 0 && (
        <Command.Group heading={t('converter.favorites')} className={groupHeading}>
          {favorites.map((c) => (
            <Row key={c} code={c} group="fav" selected={c === selected} onSelect={onSelect} />
          ))}
        </Command.Group>
      )}
      {!searching && (
        <Command.Group heading={t('converter.popularList')} className={groupHeading}>
          {POPULAR_CURRENCIES.filter((c) => !favorites.includes(c)).map((c) => (
            <Row key={c} code={c} group="pop" selected={c === selected} onSelect={onSelect} />
          ))}
        </Command.Group>
      )}
      <Command.Group heading={t('converter.all')} className={groupHeading}>
        {CURRENCIES.map(({ code }) => (
          <Row key={code} code={code} group="all" selected={code === selected} onSelect={onSelect} />
        ))}
      </Command.Group>
    </>
  )
}
